import {
  GLOSSARY_PROPOSAL_STATUSES,
  type GlossaryProposalStatus,
} from '@sbc/domain-models/knowledge-base';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGlossaryProposals } from '@/lib/api/knowledge-base';

import { ProposalStatusBadge } from './kb-badges';

export function GlossaryProposalsPage() {
  const [status, setStatus] = useState<GlossaryProposalStatus>('pending');
  const proposalsQuery = useGlossaryProposals(status);
  const proposals = proposalsQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Glossary proposals</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          EN↔KR terms the translator proposed. Approving one adds it to{' '}
          <code>glossary/en-kr-terms</code>.
        </p>
      </div>

      <div className="flex gap-2">
        {GLOSSARY_PROPOSAL_STATUSES.map((option) => (
          <Button
            key={option}
            type="button"
            size="sm"
            variant={option === status ? 'default' : 'outline'}
            onClick={() => setStatus(option)}
          >
            {option}
          </Button>
        ))}
      </div>

      {proposalsQuery.isPending && <p className="text-sm">Loading…</p>}
      {proposalsQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load the proposals: {proposalsQuery.error.message}
        </p>
      )}

      {proposalsQuery.data && (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>English</TableHead>
                <TableHead>Korean</TableHead>
                <TableHead>Gloss</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Proposed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {proposals.length ? (
                proposals.map((proposal) => (
                  <TableRow key={proposal.id}>
                    <TableCell>
                      <Link
                        to="/knowledge-base/glossary-proposals/$proposalId"
                        params={{ proposalId: proposal.id }}
                        className="font-medium hover:underline"
                      >
                        {proposal.en}
                      </Link>
                    </TableCell>
                    <TableCell>{proposal.ko}</TableCell>
                    <TableCell className="text-sm">{proposal.gloss}</TableCell>
                    <TableCell>
                      <ProposalStatusBadge status={proposal.status} />
                    </TableCell>
                    <TableCell className="text-sm">
                      {new Date(proposal.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="h-24 text-center">
                    No {status} proposals.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
